import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import axios from 'axios';
import type { ReadRequestOptions } from '../requestOptions';

/**
 * Per-order channel-ledger financial summary, backing the order-view Settlement tab.
 * Mirrors channel_integrations `OrderFinancialSummaryResource`. Decimal amounts arrive as
 * fixed-precision strings (e.g. "229.460000") — coerce with Number() at render time.
 */
export interface OrderLedgerSummary {
  astur_order_id: number;
  channel_order_id: string;
  provider: string;
  channel_name: string;
  currency: string;
  is_multi_currency: boolean;
  buckets: {
    gross_principal: string;
    commission: string;
    shipping: string;
    tax: string;
    refunds: string;
    adjustments: string;
    other: string;
  };
  net_payout: string;
  reconciliation: {
    /**
     * One of LEDGER_RECONCILIATION_STATUSES (getLedgerOrderSummaries). Typed as `string`, not the
     * union, so a status the backend adds before the frontend does is not narrowed away.
     */
    status: string;
    discrepancy_flag: boolean;
    channel_settled: string;
    astur_subtotal: string | null;
    /** Order-level item discount netted out of the subtotal so both sides share the discounted basis. */
    astur_item_discount: string | null;
    discrepancy_amount: string | null;
    expected_commission: string | null;
  };
  resolution_status: string;
  entry_count: number;
  statement_count: number;
  fee_map_version: string | null;
  first_occurred_at: string | null;
  last_occurred_at: string | null;
  computed_at: string | null;
}

/**
 * Fetches the settlement summary for one Astur order. A 404 means the order has no marketplace
 * settlement (native/non-collected channels) — returned as `data: null`, not an error, so the tab
 * renders its empty state rather than a failure.
 */
export async function getOrderLedgerSummary(
  scope: RequestScope,
  args: { subscriptionId: number; asturOrderId: number },
  options: ReadRequestOptions
): Promise<ChannelApiResponse<OrderLedgerSummary | null>> {
  try {
    if (!args.subscriptionId || args.subscriptionId < 1) {
      throw new Error('Valid subscriptionId is required');
    }
    if (!args.asturOrderId || args.asturOrderId < 1) {
      throw new Error('Valid asturOrderId is required');
    }

    const res = await scopedChannelApi(scope).get<OrderLedgerSummary>(`astur/integrations/channel/ledger/order-summary/${args.asturOrderId}`, {
      ...options,
      headers: { 'x-astur-subscription-id': args.subscriptionId.toString() },
    });
    // The backend returns the resource unwrapped (withoutWrapping); tolerate a { data } envelope
    // too so a future serialization change can't silently break the tab.
    const envelope = res.data as unknown as { data?: OrderLedgerSummary };
    const summary = envelope && envelope.data ? envelope.data : (res.data as OrderLedgerSummary);
    return { data: summary, status: res.status };
  } catch (e) {
    if (axios.isAxiosError(e) && e.response?.status === 404) {
      return { data: null, status: 404 };
    }
    return utils.error.handleAxiosError(e);
  }
}
